/**
 * Alias (ləqəb) idarəsi.
 *
 * NİYƏ AYRICA KOMPONENT
 * Admin ekranı onsuz da 440 sətirdir və alias-ın öz vəziyyəti var (siyahı,
 * forma, xətalar). Ora yamamaq həm faylı, həm də təsəvvürü ağırlaşdırardı.
 *
 * NƏ ÜÇÜNDÜR
 * Kiçik şirkət beş nəfərlik komandaya on ünvan istəyir — `info@`, `satis@`,
 * `destek@`, `muhasibat@` — amma hər biri üçün ayrıca qutu (və ayrıca pul)
 * ödəmək istəmir. Alias məhz budur: ünvan var, qutu yoxdur.
 *
 * ⚠️ EYNİ MƏNBƏYƏ BİR NEÇƏ HƏDƏF VERMƏK OLAR və bu, PAYLAMA SİYAHISIDIR:
 * `satis@` iki nəfərə baxırsa, məktub HƏR İKİSİNƏ kopyalanır. İnterfeys
 * bunu gizlətmir — eyni mənbə qruplaşdırılıb göstərilir ki, istifadəçi
 * «niyə iki nüsxə gəldi» sualını verməsin.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { Icon } from './Icon';
import { useDialog } from './Dialog';
import type { Alias, Domain, Mailbox } from '../types';

interface Props {
  domains: Domain[];
  mailboxes: Mailbox[];
}

const MSG: Record<string, string> = {
  invalid_source: 'Ünvanın adı yalnız hərf, rəqəm, nöqtə, defis və «+» ola bilər.',
  invalid_destination: 'Hədəf ünvan düzgün deyil.',
  domain_not_found: 'Bu domen sizə aid deyil.',
  domain_not_active: 'Domen hələ aktiv deyil — əvvəlcə DNS yoxlamasını keçirin.',
  source_is_mailbox: 'Bu ünvan artıq poçt qutusudur. Alias mövcud qutunun üstünə qurula bilməz.',
  exists: 'Bu alias artıq var.',
  limit_reached: 'Alias limiti dolub (100).',
  external_not_allowed: 'Hədəf öz poçt qutularınızdan biri olmalıdır — kənar ünvana yönləndirmə hələ açıq deyil.',
  loop: 'Ünvan özünə yönləndirilə bilməz.',
};

export function Aliases({ domains, mailboxes }: Props): React.ReactElement {
  const [rows, setRows] = useState<Alias[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [local, setLocal] = useState('');
  const [domainId, setDomainId] = useState('');
  const [dest, setDest] = useState('');
  // ⚠️ `window.confirm` əvəzi — bax components/Dialog.tsx
  const dlg = useDialog();

  const active = domains.filter((d) => d.active);

  const load = useCallback(async () => {
    try {
      const r = await api.aliases();
      setRows(r.aliases);
    } catch {
      setError('Alias siyahısı yüklənmədi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Susmaya görə ilk aktiv domen və ilk qutu — boş forma ilə başlamaq
  // istifadəçini iki əlavə klikə məcbur edərdi.
  useEffect(() => {
    if (!domainId && active[0]) setDomainId(active[0].id);
    if (!dest && mailboxes[0]) setDest(mailboxes[0].email);
  }, [active, mailboxes, domainId, dest]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const dom = domains.find((d) => d.id === domainId);
    if (!dom || !local.trim() || !dest) return;
    setBusy(true);
    setError(null);
    try {
      await api.createAlias(`${local.trim().toLowerCase()}@${dom.domain}`, dest);
      setLocal('');
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setError(MSG[code] ?? 'Alias yaradıla bilmədi.');
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch {
      setError('Əməliyyat alınmadı.');
    } finally {
      setBusy(false);
    }
  }

  // Eyni mənbəni bir yerə yığırıq — paylama siyahısı belə görünməlidir.
  const grouped = rows.reduce<Map<string, Alias[]>>((m, a) => {
    const list = m.get(a.source) ?? [];
    list.push(a);
    m.set(a.source, list);
    return m;
  }, new Map());

  return (
    <section className="admin-card">
      {dlg.node}
      <h2>Ünvan ləqəbləri</h2>
      <p className="admin-lead">
        Əlavə qutu açmadan yeni ünvan yaradın. <code>info@</code> ünvanına gələn
        məktub sizin qutunuza düşür və ona <strong>həmin ünvandan cavab</strong>{' '}
        yaza bilərsiniz.
      </p>

      {error ? (
        <p className="login-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="spinner" aria-label="Yüklənir" />
      ) : grouped.size === 0 ? (
        <p className="admin-empty">Hələ ləqəb yoxdur.</p>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Ünvan</th>
                <th>Kimə çatır</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...grouped.entries()].map(([source, list]) => (
                <tr key={source}>
                  <td>
                    <code>{source}</code>
                    {/*
                      ⚠️ PAYLAMA SİYAHISI AÇIQ İŞARƏLƏNİR. İki hədəfi olan
                      alias sadəcə «iki sətir» kimi görünsə, istifadəçi
                      məktubun kopyalandığını başa düşmür.
                    */}
                    {list.length > 1 ? (
                      <span className="alias-tag">{list.length} nəfərə</span>
                    ) : null}
                  </td>
                  <td>
                    <ul className="alias-dests">
                      {list.map((a) => (
                        <li key={a.id} className={a.active ? '' : 'is-off'}>
                          <code>{a.destination}</code>
                          {!a.active && <span className="alias-off">söndürülüb</span>}
                          <button
                            className="folder-tool"
                            title={a.active ? 'Söndür' : 'Yenidən aç'}
                            aria-label={`${a.source} → ${a.destination}: ${a.active ? 'söndür' : 'aç'}`}
                            disabled={busy}
                            onClick={() => void act(() => api.setAliasActive(a.id, !a.active))}
                          >
                            <Icon name={a.active ? 'eye-off' : 'eye'} size={14} />
                          </button>
                          <button
                            className="folder-tool is-danger"
                            title="Sil"
                            aria-label={`${a.source} → ${a.destination}: sil`}
                            disabled={busy}
                            onClick={() => {
                              void dlg
                                .ask({
                                  title: 'Ləqəb silinsin?',
                                  body: `«${a.source}» → «${a.destination}». Bu ünvana gələn məktublar artıq çatmayacaq.`,
                                  confirmLabel: 'Sil',
                                  danger: true,
                                })
                                .then((ok) => {
                                  if (ok === null) return;
                                  return act(() => api.deleteAlias(a.id));
                                });
                            }}
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        FORMA YALNIZ ŞƏRTLƏR ÖDƏNƏNDƏ GÖSTƏRİLİR.
        ⚠️ Aktiv domen və ya qutu yoxdursa, forma doldurula bilməz və
        istifadəçi səbəbini anlamayan xəta alır. Səbəbi ƏVVƏLCƏDƏN deyirik.
      */}
      {active.length === 0 ? (
        <p className="admin-empty">Ləqəb üçün əvvəlcə aktiv domen lazımdır.</p>
      ) : mailboxes.length === 0 ? (
        <p className="admin-empty">Ləqəb üçün əvvəlcə ən azı bir poçt qutusu yaradın.</p>
      ) : (
        <form className="alias-form" onSubmit={submit}>
          <label className="field">
            <span>Yeni ünvan</span>
            <div className="field-addon">
              <input
                required
                placeholder="satis"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
              />
              <select
                className="addon addon-select"
                value={domainId}
                onChange={(e) => setDomainId(e.target.value)}
                aria-label="Domen"
              >
                {active.map((d) => (
                  <option key={d.id} value={d.id}>
                    @{d.domain}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="field">
            <span>Kimə çatsın</span>
            <select value={dest} onChange={(e) => setDest(e.target.value)}>
              {mailboxes.map((m) => (
                <option key={m.id} value={m.email}>
                  {m.email}
                </option>
              ))}
            </select>
            <small className="field-hint">
              Eyni ünvanı bir neçə qutuya yönləndirsəniz, məktub hamısına gedir.
            </small>
          </label>

          <button type="submit" className="btn-primary" disabled={busy || !local.trim()}>
            {busy ? 'Yaradılır…' : 'Ləqəb əlavə et'}
          </button>
        </form>
      )}
    </section>
  );
}
